import React, { useState, useEffect } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils/utils';
import { useDateFormatter } from '@/shared/hooks';
import { Button } from '@/shared/components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/Popover';
import { Label } from '@/shared/components/ui/Label';
import { Calendar } from '@/shared/components/ui/Calendar';

interface DatePickerInputProps {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  title?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  inputClassName?: string;
  error?: string;
  format?: 'short' | 'medium' | 'long';
}

export const DatePickerInput: React.FC<DatePickerInputProps> = ({
  value,
  onChange,
  placeholder,
  title,
  description,
  required = false,
  disabled = false,
  minDate,
  maxDate,
  className = '',
  inputClassName = '',
  error,
  format: dateFormat = 'medium',
}) => {
  const { t } = useTranslation('common');
  const { formatDate } = useDateFormatter();
  const [selectedDate, setSelectedDate] = useState<Date | null>(value || null);
  const [isOpen, setIsOpen] = useState(false);

  // Use translation for placeholder if not provided
  const displayPlaceholder = placeholder || t('pickDate');

  // Update selected date when value prop changes
  useEffect(() => {
    setSelectedDate(value || null);
  }, [value]);

  const formatDateLocal = (date: Date, formatType: string): string => {
    return formatDate(date, formatType as 'short' | 'medium' | 'long');
  };

  const handleDateSelect = (date: Date | undefined) => {
    const selectedDate = date || null;
    setSelectedDate(selectedDate);
    onChange(selectedDate);
    setIsOpen(false);
  };

  const clearDate = () => {
    setSelectedDate(null);
    onChange(null);
    setIsOpen(false);
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today);
    onChange(today);
    setIsOpen(false);
  };

  // Create disabled date function for Calendar component
  const isDateDisabled = (date: Date) => {
    return (minDate && date < minDate) || (maxDate && date > maxDate) || false;
  };

  return (
    <div className={cn('w-full', className)}>
      {(title || description) && (
        <div className="mb-2 space-y-1">
          {title && (
            <Label className="text-sm font-medium text-foreground">
              {title}
              {required && <span className="text-destructive ms-1">*</span>}
            </Label>
          )}
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      )}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              'w-full justify-between text-start font-normal h-10',
              !selectedDate && 'text-muted-foreground',
              error &&
                'border-destructive focus:ring-destructive focus:border-destructive',
              inputClassName
            )}
          >
            <span className="truncate">
              {selectedDate
                ? formatDateLocal(selectedDate, dateFormat)
                : displayPlaceholder}
            </span>
            <CalendarIcon className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate || undefined}
            onSelect={handleDateSelect}
            disabled={isDateDisabled}
            initialFocus
            fixedWeeks
            className="rounded-lg border shadow-sm"
          />
          <div className="flex justify-between gap-2 px-3 pb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToToday}
              className="text-sm"
            >
              {t('today')}
            </Button>
            {selectedDate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearDate}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                {t('clear')}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default DatePickerInput;
